/**
 * StaticPageHero — Renders an admin-edited static page (Title + subtitle +
 * optional hero image + Markdown-flavoured body).
 *
 * The parent page passes a slug. The component fetches /landing/pages/{slug}
 * and returns:
 *   { is_default, node }
 * - is_default: true when the admin has not customised this page yet (the
 *   parent can choose to render its built-in rich layout instead).
 * - node: the JSX block to render (always provided).
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny markdown parser — only ## headings + paragraphs + line breaks.
// Uses a deterministic block-content key so React reconciliation is stable
// even if the markdown source is edited (Admin CMS).
function _blockKey(block, i) {
    let hash = 0;
    for (let j = 0; j < block.length; j++) hash = ((hash << 5) - hash + block.charCodeAt(j)) | 0;
    return `mb_${i}_${hash}`;
}

function _isBullet(line) {
    return /^\s*[-*]\s+/.test(line);
}

function _renderInline(text) {
    // Support **bold** inline markdown.
    if (!text.includes("**")) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, idx) => {
        if (p.startsWith("**") && p.endsWith("**")) {
            return <strong key={`b_${idx}`} className="font-semibold text-[#0F3311]">{p.slice(2, -2)}</strong>;
        }
        return p;
    });
}

function _renderLines(lines, keyPrefix) {
    // Group consecutive bullet lines into a <ul>; render others as <p>.
    const out = [];
    let i = 0;
    while (i < lines.length) {
        if (_isBullet(lines[i])) {
            const items = [];
            while (i < lines.length && _isBullet(lines[i])) {
                items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
                i++;
            }
            out.push(
                <ul key={`${keyPrefix}_ul_${i}`} className="mt-3 ml-5 list-disc space-y-1.5 text-[15.5px] text-[#4A5D4E] leading-relaxed">
                    {items.map((it, j) => (
                        <li key={`${keyPrefix}_li_${j}`} className="pl-1">{_renderInline(it)}</li>
                    ))}
                </ul>
            );
        } else if (lines[i].trim()) {
            out.push(
                <p key={`${keyPrefix}_p_${i}`} className="text-[15.5px] text-[#4A5D4E] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] mt-3 first:mt-0">
                    {_renderInline(lines[i])}
                </p>
            );
            i++;
        } else {
            i++;
        }
    }
    return out;
}

const SLOT_RE = /^\[\[slot:([a-z0-9-]+)\]\]$/;

function renderMarkdown(body, slots) {
    if (!body) return null;
    const blocks = body.split(/\n{2,}/);
    return blocks.map((block, i) => {
        const k = _blockKey(block, i);
        // [[slot:name]] on its own line → render the React element supplied by the page.
        const slotMatch = block.trim().match(SLOT_RE);
        if (slotMatch) {
            const node = slots?.[slotMatch[1]];
            return node ? <div key={k} className="mt-6" data-testid={`static-slot-${slotMatch[1]}`}>{node}</div> : null;
        }
        const lines = block.split("\n");
        if (lines[0].startsWith("#### ")) {
            const heading = lines[0].slice(5);
            const restLines = lines.slice(1);
            return (
                <section key={k} className="mt-4 first:mt-0">
                    <h4 className="text-[15px] md:text-[16px] font-semibold text-[#0F3311]">{heading}</h4>
                    {restLines.length > 0 && _renderLines(restLines, k)}
                </section>
            );
        }
        if (lines[0].startsWith("### ")) {
            const heading = lines[0].slice(4);
            const restLines = lines.slice(1);
            return (
                <section key={k} className="mt-5 first:mt-0">
                    <h3 className="text-[17px] md:text-[19px] font-bold text-[#0F3311]">{heading}</h3>
                    {restLines.length > 0 && _renderLines(restLines, k)}
                </section>
            );
        }
        if (lines[0].startsWith("## ")) {
            const heading = lines[0].slice(3);
            const restLines = lines.slice(1);
            return (
                <section key={k} className="mt-7 first:mt-0">
                    <h2 className="text-[22px] md:text-[24px] font-bold text-[#0F3311]">{heading}</h2>
                    {restLines.length > 0 && _renderLines(restLines, k)}
                </section>
            );
        }
        if (lines[0].startsWith("# ")) {
            return (
                <h1 key={k} className="text-3xl font-bold text-[#0F3311] mt-7 first:mt-0">
                    {lines[0].slice(2)}
                </h1>
            );
        }
        // Bullet-only block or paragraph block.
        if (lines.some(_isBullet)) {
            return <div key={k}>{_renderLines(lines, k)}</div>;
        }
        return (
            <p key={k} className="text-[15.5px] text-[#4A5D4E] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] mt-3 first:mt-0">
                {_renderInline(block)}
            </p>
        );
    });
}

export function useStaticPage(slug) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/landing/pages/${slug}`)
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch(() => { if (!cancelled) setData({ is_default: true, title: null, body: null }); });
        return () => { cancelled = true; };
    }, [slug]);

    // SEO: give every static/footer page a UNIQUE <title> + <meta description>
    // (fixes duplicate-title / duplicate-description audit findings centrally).
    useEffect(() => {
        if (!data || !data.title) return;
        const prevTitle = document.title;
        const meta = document.querySelector('meta[name="description"]');
        const prevDesc = meta?.getAttribute("content");
        document.title = `${data.title} — Family Repository`;
        let desc = (data.hero_subtitle || "").trim();
        if (!desc && data.body) {
            desc = String(data.body).replace(/[#*>_`-]/g, " ").replace(/\s+/g, " ").trim();
        }
        if (desc && meta) meta.setAttribute("content", desc.slice(0, 158));
        return () => {
            document.title = prevTitle;
            if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
        };
    }, [data]);

    return data;
}

export function StaticPageRenderer({ data, defaultLayout, hideHero, hideBody, slots }) {
    if (!data) {
        return (
            <div className="animate-pulse">
                <div className="h-12 w-2/3 bg-[#1B5E20]/10 rounded mb-4" />
                <div className="h-5 w-full bg-[#1B5E20]/5 rounded mb-2" />
                <div className="h-5 w-5/6 bg-[#1B5E20]/5 rounded" />
            </div>
        );
    }
    if (data.is_default && defaultLayout) {
        // Admin hasn't customised — show the rich built-in default layout instead of plain markdown.
        return defaultLayout;
    }

    // HERO BANNER MODE — full-bleed image with gradient overlay + title text on top.
    // Applies to every static/footer page that has a hero_image_url set by the admin.
    if (!hideHero && data.hero_image_url) {
        return (
            <article data-testid={`static-page-${data.slug || "page"}`}>
                {/* Full-bleed hero banner — breaks out of the max-width container */}
                <div
                    className="relative overflow-hidden h-[260px] sm:h-[360px] md:h-[430px]"
                    style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
                >
                    <img
                        src={data.hero_image_url}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    {/* Gradient overlay — dark at bottom for text legibility */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/28 to-transparent" />
                    {/* Title + subtitle overlaid on the banner */}
                    <div className="absolute bottom-0 left-0 right-0 pb-8 sm:pb-10 px-5 sm:px-8 lg:px-16">
                        <div className="max-w-4xl mx-auto">
                            <h1
                                className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1]"
                                style={{ textShadow: "0 2px 6px rgba(0,0,0,0.40)" }}
                            >
                                {data.title}
                            </h1>
                            {data.hero_subtitle && (
                                <p className="mt-3 text-[15px] sm:text-[17px] text-white/85 font-medium leading-relaxed max-w-2xl">
                                    {data.hero_subtitle}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                {/* Body content below the banner */}
                {!hideBody && (
                    <div className="mt-9 min-w-0 break-words [overflow-wrap:anywhere]">
                        {renderMarkdown(data.body, slots)}
                    </div>
                )}
            </article>
        );
    }

    return (
        <article data-testid={`static-page-${data.slug || "page"}`}>
            {!hideHero && (
                /* No image — text-only card hero */
                <div className="relative overflow-hidden rounded-3xl border border-[#E3EBE0] bg-gradient-to-br from-[#F4F8F1] via-[#EEF5EB] to-[#E4EFE1]">
                    <div className="px-6 sm:px-10 py-9 sm:py-14">
                        <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold text-[#0F3311] leading-[1.12] tracking-tight">
                            {data.title}
                        </h1>
                        {data.hero_subtitle && (
                            <p className="mt-4 text-[15.5px] sm:text-[17px] text-[#4A5D4E] leading-relaxed max-w-xl">
                                {data.hero_subtitle}
                            </p>
                        )}
                    </div>
                </div>
            )}
            {!hideBody && (
                <div className={`min-w-0 break-words [overflow-wrap:anywhere] ${hideHero ? "" : "mt-9"}`}>{renderMarkdown(data.body, slots)}</div>
            )}
        </article>
    );
}

